/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Audit Logs Page - SuperAdmin Portal
 * ============================================================================
 *
 * View and filter platform-wide audit logs for security monitoring,
 * compliance tracking, and troubleshooting.
 *
 * ============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClipboardList, Filter, RefreshCw, Download,
  CheckCircle, XCircle, AlertTriangle, Clock, User,
  Building2, CreditCard, Briefcase, Settings, Shield,
  ChevronLeft, ChevronRight, Calendar, Globe
} from 'lucide-react';
import { SortableHeader, useSort } from '@/components/ui/SortableHeader';
import { SearchInput } from '@/components/ui';

// Types
interface AuditLog {
  id: string;
  action: string;
  actionCategory: string;
  description: string;
  userEmail: string;
  userRole: string;
  entityType: string;
  entityId: string;
  entityName: string;
  orgName: string;
  ipAddress: string;
  result: 'SUCCESS' | 'FAILURE' | 'PARTIAL';
  createdAt: string;
}

// Result Badge Component
const ResultBadge: React.FC<{ result: AuditLog['result']; t: (key: string) => string }> = ({ result, t }) => {
  const config = {
    SUCCESS: { bg: 'bg-emerald-500/15', text: 'text-emerald-500', icon: CheckCircle },
    FAILURE: { bg: 'bg-red-500/15', text: 'text-red-500', icon: XCircle },
    PARTIAL: { bg: 'bg-amber-500/15', text: 'text-amber-500', icon: AlertTriangle },
  };
  const { bg, text, icon: Icon } = config[result];

  return (
    <span className={`result-badge ${bg} ${text}`}>
      <Icon className="w-3 h-3" />
      {t(`auditLogs.results.${result.toLowerCase()}`)}
    </span>
  );
};

// Category Badge Component
const CategoryBadge: React.FC<{ category: string; t: (key: string) => string }> = ({ category, t }) => {
  const config: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
    AUTH: { bg: 'bg-blue-500/15', text: 'text-blue-500', icon: <Shield className="w-3 h-3" /> },
    ORG: { bg: 'bg-purple-500/15', text: 'text-purple-500', icon: <Building2 className="w-3 h-3" /> },
    BILLING: { bg: 'bg-emerald-500/15', text: 'text-emerald-500', icon: <CreditCard className="w-3 h-3" /> },
    PARTNER: { bg: 'bg-orange-500/15', text: 'text-orange-500', icon: <Briefcase className="w-3 h-3" /> },
    SYSTEM: { bg: 'bg-gray-500/15', text: 'text-gray-400', icon: <Settings className="w-3 h-3" /> },
  };
  const { bg, text, icon } = config[category] || config.SYSTEM;

  return (
    <span className={`category-badge ${bg} ${text}`}>
      {icon}
      {t(`auditLogs.categories.${category.toLowerCase()}`)}
    </span>
  );
};

// Stat Card Component
const StatCard: React.FC<{
  icon: React.ReactNode;
  value: string | number;
  label: string;
  color: string;
}> = ({ icon, value, label, color }) => (
  <div className="stat-card">
    <div className={`stat-icon ${color}`}>{icon}</div>
    <div className="stat-value">{value}</div>
    <div className="stat-label">{label}</div>
  </div>
);

// Main Audit Logs Page
export default function AuditLogsPage() {
  const { t } = useTranslation('platform');
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [resultFilter, setResultFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Sort state
  const { sortConfig, handleSort, getSortParam } = useSort('createdAt', 'desc');

  const categories = ['AUTH', 'ORG', 'BILLING', 'PARTNER', 'SYSTEM'];
  const results = ['SUCCESS', 'FAILURE', 'PARTIAL'];

  // Stats state
  const [stats, setStats] = useState({ totalEvents: 0, successCount: 0, failureCount: 0, todayCount: 0 });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    const sortParam = getSortParam() || 'createdAt,desc';
    try {
      let url = `/api/v1/superadmin/audit-logs?page=${page}&size=50&sort=${sortParam}`;

      if (categoryFilter) {
        url = `/api/v1/superadmin/audit-logs/category/${categoryFilter}?page=${page}&size=50`;
      } else if (actionFilter) {
        url = `/api/v1/superadmin/audit-logs/action/${actionFilter}?page=${page}&size=50`;
      } else if (dateFrom && dateTo) {
        url = `/api/v1/superadmin/audit-logs/date-range?startDate=${dateFrom}T00:00:00Z&endDate=${dateTo}T23:59:59Z&page=${page}&size=50`;
      }

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setLogs(data.data.content || []);
        setTotalPages(data.data.page?.totalPages || 1);

        // Calculate stats from response
        const content = data.data.content || [];
        const today = new Date().toDateString();
        setStats({
          totalEvents: data.data.page?.totalElements || content.length,
          successCount: content.filter((l: AuditLog) => l.result === 'SUCCESS').length,
          failureCount: content.filter((l: AuditLog) => l.result === 'FAILURE').length,
          todayCount: content.filter((l: AuditLog) => new Date(l.createdAt).toDateString() === today).length,
        });
      } else {
        setLogs([]);
        setTotalPages(1);
      }
    } catch {
      setLogs([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, categoryFilter, actionFilter, dateFrom, dateTo, getSortParam]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearch = () => {
    setPage(0);
    fetchLogs();
  };

  const clearFilters = () => {
    setCategoryFilter('');
    setActionFilter('');
    setResultFilter('');
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAction = (action: string) => {
    return action.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  };


  return (
    <div className="audit-logs-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <ClipboardList className="w-6 h-6" /> {t('auditLogs.title')}
          </h1>
          <p className="page-subtitle">{t('auditLogs.subtitle')}</p>
        </div>
        <div className="header-actions">
          <button onClick={fetchLogs} className="btn btn-secondary">
            <RefreshCw className="w-4 h-4" />
            {t('common.refresh')}
          </button>
          <button className="btn btn-secondary">
            <Download className="w-4 h-4" />
            {t('common.export')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <StatCard
          icon={<ClipboardList className="w-5 h-5" />}
          value={stats.totalEvents}
          label={t('auditLogs.stats.totalEvents')}
          color="stat-icon-blue"
        />
        <StatCard
          icon={<CheckCircle className="w-5 h-5" />}
          value={stats.successCount}
          label={t('auditLogs.stats.successful')}
          color="stat-icon-green"
        />
        <StatCard
          icon={<XCircle className="w-5 h-5" />}
          value={stats.failureCount}
          label={t('auditLogs.stats.failed')}
          color="stat-icon-red"
        />
        <StatCard
          icon={<Clock className="w-5 h-5" />}
          value={stats.todayCount}
          label={t('auditLogs.stats.today')}
          color="stat-icon-purple"
        />
      </div>

      {/* Filters */}
      <div className="filters-card">
        <div className="filters-header">
          <Filter className="w-5 h-5" />
          <span>{t('auditLogs.filters.title')}</span>
        </div>
        <div className="filters-grid">
          <div className="filter-group">
            <label>{t('auditLogs.filters.search')}</label>
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={t('auditLogs.filters.searchPlaceholder')}
            />
          </div>
          <div className="filter-group">
            <label>{t('auditLogs.filters.category')}</label>
            <select
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
              className="filter-input"
            >
              <option value="">{t('auditLogs.filters.allCategories')}</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{t(`auditLogs.categories.${cat.toLowerCase()}`)}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>{t('auditLogs.filters.result')}</label>
            <select
              value={resultFilter}
              onChange={(e) => { setResultFilter(e.target.value); setPage(0); }}
              className="filter-input"
            >
              <option value="">{t('auditLogs.filters.allResults')}</option>
              {results.map(r => (
                <option key={r} value={r}>{t(`auditLogs.results.${r.toLowerCase()}`)}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>{t('auditLogs.filters.from')}</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label>{t('auditLogs.filters.to')}</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
              className="filter-input"
            />
          </div>
          <div className="filter-group filter-actions">
            <label>&nbsp;</label>
            <button onClick={clearFilters} className="btn btn-outline">
              {t('auditLogs.filters.clear')}
            </button>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="logs-table-card">
        <div className="table-header">
          <span className="table-col col-time">
            <SortableHeader
              columnKey="createdAt"
              label={t('auditLogs.table.time')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </span>
          <span className="table-col col-category">
            <SortableHeader
              columnKey="actionCategory"
              label={t('auditLogs.table.category')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </span>
          <span className="table-col col-action">
            <SortableHeader
              columnKey="action"
              label={t('auditLogs.table.action')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </span>
          <span className="table-col col-description">{t('auditLogs.table.description')}</span>
          <span className="table-col col-user">
            <SortableHeader
              columnKey="userEmail"
              label={t('auditLogs.table.user')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </span>
          <span className="table-col col-ip">{t('auditLogs.table.ipAddress')}</span>
          <span className="table-col col-result">
            <SortableHeader
              columnKey="result"
              label={t('auditLogs.table.result')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </span>
        </div>

        <div className="table-body">
          {loading ? (
            <div className="table-loading">
              <div className="spinner" />
              <span>{t('auditLogs.loading')}</span>
            </div>
          ) : logs.length === 0 ? (
            <div className="table-empty">
              <ClipboardList className="w-12 h-12" />
              <p>{t('auditLogs.empty')}</p>
              <span>{t('auditLogs.emptyHint')}</span>
            </div>
          ) : (
            logs.map((log) => (
              <div key={log.id} className="table-row">
                <div className="table-col col-time">
                  <Clock className="w-3.5 h-3.5 text-muted" />
                  <span>{formatDate(log.createdAt)}</span>
                </div>
                <div className="table-col col-category">
                  <CategoryBadge category={log.actionCategory} t={t} />
                </div>
                <div className="table-col col-action">
                  {formatAction(log.action)}
                </div>
                <div className="table-col col-description" title={log.description}>
                  {log.description}
                </div>
                <div className="table-col col-user">
                  <div className="user-info">
                    <span className="user-email">{log.userEmail || '-'}</span>
                    {log.userRole && <span className="user-role">{log.userRole}</span>}
                  </div>
                </div>
                <div className="table-col col-ip">
                  <Globe className="w-3.5 h-3.5 text-muted" />
                  <span className="ip-address">{log.ipAddress || '-'}</span>
                </div>
                <div className="table-col col-result">
                  <ResultBadge result={log.result} t={t} />
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="table-pagination">
            <span className="pagination-info">{t('auditLogs.pagination.page', { current: page + 1, total: totalPages })}</span>
            <div className="pagination-buttons">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="pagination-btn"
              >
                <ChevronLeft className="w-4 h-4" />
                {t('auditLogs.pagination.previous')}
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="pagination-btn"
              >
                {t('auditLogs.pagination.next')}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{auditLogsStyles}</style>
    </div>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const auditLogsStyles = `
  .audit-logs-page {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  /* Page Header */
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .page-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: var(--font-heading);
    font-size: 22px;
    font-weight: 700;
    color: var(--color-text-primary);
  }

  .page-subtitle {
    font-size: 13px;
    color: var(--color-text-secondary);
    margin-top: 4px;
  }

  .header-actions {
    display: flex;
    gap: 12px;
  }

  /* Button */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0 16px;
    height: 40px;
    font-size: 13px;
    font-weight: 500;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-secondary {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    color: var(--color-text-primary);
  }

  .btn-secondary:hover {
    background: var(--color-bg-hover);
  }

  .btn-outline {
    background: transparent;
    border: 1px solid var(--color-border-subtle);
    color: var(--color-text-secondary);
  }

  .btn-outline:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  /* Stats Grid */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }

  .stat-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    padding: 16px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    margin-bottom: 12px;
  }

  .stat-icon-blue { background: rgba(59, 130, 246, 0.15); color: #3B82F6; }
  .stat-icon-green { background: rgba(16, 185, 129, 0.15); color: #10B981; }
  .stat-icon-red { background: rgba(239, 68, 68, 0.15); color: #EF4444; }
  .stat-icon-purple { background: rgba(139, 92, 246, 0.15); color: #8B5CF6; }

  .stat-value {
    font-size: 24px;
    font-weight: 700;
    font-family: var(--font-mono);
    color: var(--color-text-primary);
  }

  .stat-label {
    font-size: 12px;
    color: var(--color-text-muted);
    margin-top: 4px;
  }

  /* Filters Card */
  .filters-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    padding: 16px 20px;
  }

  .filters-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin-bottom: 16px;
  }

  .filters-grid {
    display: grid;
    grid-template-columns: 2fr repeat(4, 1fr) auto;
    gap: 16px;
    align-items: end;
  }

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .filter-group label {
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-muted);
  }

  .filter-input {
    height: 40px;
    padding: 0 12px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    font-size: 13px;
    color: var(--color-text-primary);
    outline: none;
    transition: border-color 0.15s ease;
  }

  .filter-input:focus {
    border-color: var(--color-brand-primary);
  }

  .filter-actions {
    min-width: 80px;
  }

  .filter-actions .btn {
    width: 100%;
    justify-content: center;
  }

  /* Logs Table */
  .logs-table-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    overflow: hidden;
  }

  .table-header {
    display: grid;
    grid-template-columns: 140px 100px 140px 1fr 180px 120px 100px;
    gap: 12px;
    padding: 12px 20px;
    background: var(--color-bg-primary);
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .table-header .table-col {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-muted);
  }

  .table-body {
    max-height: 600px;
    overflow-y: auto;
  }

  .table-row {
    display: grid;
    grid-template-columns: 140px 100px 140px 1fr 180px 120px 100px;
    gap: 12px;
    padding: 14px 20px;
    border-bottom: 1px solid var(--color-border-subtle);
    align-items: center;
    transition: background 0.1s ease;
  }

  .table-row:last-child {
    border-bottom: none;
  }

  .table-row:hover {
    background: var(--color-bg-hover);
  }

  .table-col {
    font-size: 13px;
    color: var(--color-text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .col-time {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--color-text-secondary);
    font-size: 12px;
  }

  .col-action {
    font-weight: 500;
  }

  .col-description {
    color: var(--color-text-secondary);
  }

  .col-ip {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .ip-address {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--color-text-muted);
  }

  .user-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .user-email {
    font-size: 13px;
    color: var(--color-text-primary);
  }

  .user-role {
    font-size: 11px;
    color: var(--color-text-muted);
  }

  .text-muted {
    color: var(--color-text-muted);
  }

  /* Badges */
  .result-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
  }

  .category-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 10px;
    font-weight: 600;
  }

  /* Loading & Empty States */
  .table-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px;
    gap: 16px;
    color: var(--color-text-muted);
  }

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--color-border-subtle);
    border-top-color: var(--color-brand-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .table-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px;
    color: var(--color-text-muted);
    text-align: center;
  }

  .table-empty p {
    margin-top: 16px;
    font-size: 15px;
    font-weight: 500;
    color: var(--color-text-secondary);
  }

  .table-empty span {
    font-size: 13px;
    margin-top: 4px;
  }

  /* Pagination */
  .table-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    border-top: 1px solid var(--color-border-subtle);
  }

  .pagination-info {
    font-size: 13px;
    color: var(--color-text-muted);
  }

  .pagination-buttons {
    display: flex;
    gap: 8px;
  }

  .pagination-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    color: var(--color-text-primary);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .pagination-btn:hover:not(:disabled) {
    background: var(--color-bg-hover);
  }

  .pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Responsive */
  @media (max-width: 1200px) {
    .filters-grid {
      grid-template-columns: repeat(3, 1fr);
    }

    .table-header,
    .table-row {
      grid-template-columns: 120px 90px 120px 1fr 100px;
    }

    .col-ip,
    .col-user .user-role {
      display: none;
    }
  }

  @media (max-width: 768px) {
    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .filters-grid {
      grid-template-columns: 1fr 1fr;
    }

    .header-actions {
      flex-direction: column;
    }

    .table-header,
    .table-row {
      grid-template-columns: 1fr 1fr 1fr;
    }

    .col-description,
    .col-user {
      display: none;
    }
  }

  @media (max-width: 640px) {
    .stats-grid {
      grid-template-columns: 1fr 1fr;
    }

    .filters-grid {
      grid-template-columns: 1fr;
    }

    .logs-table-card {
      overflow-x: auto;
    }

    .table-header,
    .table-row {
      min-width: 480px;
    }
  }
`;
